import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowRight, Eye, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/components/reports/ReportWidgets";
import type { Task } from "@/types/task";
import type { ProposalListRow } from "@/types/proposal";
import type { AttentionRow, Bar, FunnelData, Leader, ManagerData, QuarterForecast } from "./useManagerHome";
import { dateLocale, withClock } from "@/lib/dateTime";

/** White rounded tile; the bento grid mixes sizes with col-span classes */
export function Tile({ title, subtitle, action, className, children }: { title?: string; subtitle?: string; action?: React.ReactNode; className?: string; children: React.ReactNode }) {
  return (
    <section className={cn("flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-xs", className)}>
      {(title || action) && (
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {title && <h2 className="text-[13px] font-semibold text-muted-foreground">{title}</h2>}
            {subtitle && <p className="text-xs text-muted-foreground/80">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function EmptyLine({ children }: { children: React.ReactNode }) {
  return <p className="py-6 text-center text-xs text-muted-foreground">{children}</p>;
}

/* ---------- KPI strip ---------- */

function Delta({ now, prev, unit = "%" }: { now: number; prev: number; unit?: string }) {
  if (!prev && !now) return null;
  const diff = unit === "%" ? (prev ? ((now - prev) / Math.abs(prev)) * 100 : 100) : now - prev;
  const up = diff >= 0;
  return (
    <span className={cn("text-[11.5px] font-semibold", up ? "text-success" : "text-danger")}>
      {up ? "+" : "−"}
      {Math.abs(diff).toFixed(unit === "%" ? 1 : 1)}
      {unit === "%" ? "%" : " pts"}
    </span>
  );
}

export function KpiStrip({ k, awaiting }: { k: ManagerData["kpis"]; awaiting: number | null }) {
  const items: Array<{ label: string; value: string; delta?: React.ReactNode; note?: string }> = [
    { label: "Open pipeline", value: fmtMoney(k.pipeline), note: `${fmtMoney(k.weighted)} weighted` },
    { label: "Revenue won", value: fmtMoney(k.revenue), delta: <Delta now={k.revenue} prev={k.revenuePrev} />, note: "vs last month" },
    { label: "Win rate", value: `${k.winRate.toFixed(0)}%`, delta: <Delta now={k.winRate} prev={k.winRatePrev} unit="pts" />, note: "vs last month" },
    { label: "Lead conversion", value: `${k.conversion.toFixed(0)}%`, delta: <Delta now={k.conversion} prev={k.conversionPrev} unit="pts" />, note: "vs last month" },
  ];
  if (awaiting !== null) items.push({ label: "Quotes awaiting approval", value: String(awaiting), note: "across the company" });
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      {/* Cells share hairline borders and grow to fill each row, however many there are */}
      <div className="-mb-px -mr-px flex flex-wrap">
        {items.map((i) => (
          <div key={i.label} className="flex flex-1 basis-[170px] flex-col gap-0.5 border-b border-r border-border px-4 py-3">
            <span className="text-xs font-medium text-muted-foreground">{i.label}</span>
            <span className="font-mono text-[22px] font-semibold leading-tight tracking-tight">{i.value}</span>
            <span className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
              {i.delta}
              {i.note}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Revenue / forecast chart ---------- */

const TONE: Record<string, string> = { gold: "var(--gold)", primary: "var(--primary)", soft: "color-mix(in oklab, var(--primary) 28%, transparent)", danger: "var(--danger)" };

/** This quarter by month: won (gold), committed (blue) and best case (light), with the monthly target as a dashed line */
export function ForecastTile({ forecast, className }: { forecast: QuarterForecast; className?: string }) {
  const { months, quarter } = forecast;
  const cur = months.find((m) => m.current) || months[0]!;
  const totals = months.map((m) => m.won + m.committed + m.best_case);
  const max = Math.max(1, cur.target, ...totals);
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.ceil(max / step) * step;
  const W = 600;
  const H = 200;
  const left = 46;
  const bottom = 24;
  const plotH = H - bottom - 8;
  const slot = (W - left) / months.length;
  const bw = Math.min(90, slot * 0.5);
  const y = (v: number) => 8 + plotH - (v / top) * plotH;
  const summary = months.map((m, i) => `${m.label} ${fmtMoney(totals[i] ?? 0)}`).join(", ");
  const expected = quarter.won + quarter.committed;
  return (
    <Tile className={className}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[13px] font-semibold text-muted-foreground">Quarter forecast</h2>
          <span className="font-mono text-[28px] font-semibold tracking-tight">{fmtMoney(expected)}</span>
          {quarter.target > 0 && (
            <span className="text-muted-foreground">
              {months.every((m) => m.target > 0) ? ` of ${fmtMoney(quarter.target)} target` : ` · ${cur.label} target ${fmtMoney(cur.target)}`}
            </span>
          )}
        </div>
        <div className="flex gap-3.5 text-xs text-muted-foreground">
          {[["Won", "gold"], ["Committed", "primary"], ["Best case", "soft"]].map(([label, tone]) => (
            <span key={label} className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-sm" style={{ background: TONE[tone as string] }} />
              {label}
            </span>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-[210px] w-full" role="img" aria-label={`Quarter forecast by month: ${summary}`}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={left} x2={W} y1={y(top * f)} y2={y(top * f)} stroke="var(--border)" strokeDasharray={f === 0 ? undefined : "3 4"} />
            <text x={left - 8} y={y(top * f) + 3.5} textAnchor="end" fontSize="10.5" fontFamily="var(--font-mono)" fill="var(--muted-foreground)">
              {f === 0 ? "0" : fmtMoney(top * f)}
            </text>
          </g>
        ))}
        {months.map((m, i) => {
          const x = left + slot * i + (slot - bw) / 2;
          let acc = 0;
          return (
            <g key={m.period}>
              {([["won", "gold"], ["committed", "primary"], ["best_case", "soft"]] as const).map(([k, tone]) => {
                const v = m[k];
                const y0 = y(acc + v);
                const h = Math.max(0, y(acc) - y0);
                acc += v;
                return h > 0 ? <rect key={k} x={x} y={y0} width={bw} height={h} rx={3} fill={TONE[tone]} /> : null;
              })}
              <text x={x + bw / 2} y={H - 7} textAnchor="middle" fontSize="12" fontWeight={m.current ? 700 : 500} fill={m.current ? "var(--foreground)" : "var(--muted-foreground)"}>
                {m.label}
              </text>
            </g>
          );
        })}
        {cur.target > 0 && (
          <g>
            <line x1={left} x2={W} y1={y(cur.target)} y2={y(cur.target)} stroke="var(--foreground)" strokeDasharray="4 4" />
            <text x={W - 4} y={y(cur.target) - 5} textAnchor="end" fontSize="11" fill="var(--foreground)">
              Monthly target {fmtMoney(cur.target)}
            </text>
          </g>
        )}
      </svg>
    </Tile>
  );
}

export function ChartTile({ chart, className }: { chart: ManagerData["chart"]; className?: string }) {
  const totals = chart.bars.map((b) => b.segments.reduce((s, x) => s + x.value, 0));
  const max = Math.max(1, ...totals);
  // Round the axis up to a tidy number of lakhs or crores
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.ceil(max / step) * step;
  const W = 600;
  const H = 190;
  const left = 46;
  const bottom = 24;
  const plotH = H - bottom - 8;
  const slot = (W - left) / Math.max(1, chart.bars.length);
  const bw = Math.min(58, slot * 0.6);
  const y = (v: number) => 8 + plotH - (v / top) * plotH;
  const summary = chart.bars.map((b, i) => `${b.label} ${fmtMoney(totals[i] ?? 0)}`).join(", ");

  if (totals.every((t) => t === 0)) {
    return (
      <Tile title={chart.title} subtitle={chart.subtitle} className={className}>
        <EmptyLine>Nothing to show for this period yet.</EmptyLine>
      </Tile>
    );
  }

  return (
    <Tile title={chart.title} subtitle={chart.subtitle} className={className}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-[210px] w-full" role="img" aria-label={`${chart.title}: ${summary}`}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={left} x2={W} y1={y(top * f)} y2={y(top * f)} stroke="var(--border)" strokeDasharray={f === 0 ? undefined : "3 4"} />
            <text x={left - 8} y={y(top * f) + 3.5} textAnchor="end" fontSize="10.5" fontFamily="var(--font-mono)" fill="var(--muted-foreground)">
              {f === 0 ? "0" : fmtMoney(top * f)}
            </text>
          </g>
        ))}
        {chart.bars.map((b: Bar, i) => {
          let acc = 0;
          const x = left + slot * i + (slot - bw) / 2;
          return (
            <g key={`${b.label}-${i}`}>
              {b.segments.map((s, j) => {
                const y0 = y(acc + s.value);
                const h = Math.max(0, y(acc) - y0);
                acc += s.value;
                return h > 0 ? <rect key={j} x={x} y={y0} width={bw} height={h} rx={3} fill={TONE[s.tone]} opacity={b.current || chart.bars.length <= 3 ? 1 : 0.9} /> : null;
              })}
              <text x={x + bw / 2} y={H - 7} textAnchor="middle" fontSize="11.5" fontWeight={b.current ? 700 : 500} fill={b.current ? "var(--foreground)" : "var(--muted-foreground)"}>
                {b.label}
              </text>
            </g>
          );
        })}
      </svg>
    </Tile>
  );
}

/* ---------- Target ring (navy) ---------- */

export function TargetTile({ k, daysLeft, monthName, className }: { k: ManagerData["kpis"]; daysLeft: number; monthName: string; className?: string }) {
  const has = k.target > 0;
  const pct = Math.max(0, Math.min(100, k.targetPct ?? (has ? (k.revenue / k.target) * 100 : 0)));
  const C = 2 * Math.PI * 62;
  const gap = Math.max(0, k.target - k.revenue);
  return (
    <section className={cn("flex flex-col justify-between gap-3 rounded-2xl bg-sidebar p-4 text-white", className)}>
      <h2 className="text-[13px] font-semibold text-sidebar-foreground">{monthName} target</h2>
      {has ? (
        <>
          <svg viewBox="0 0 150 150" className="mx-auto size-[150px]" role="img" aria-label={`${pct.toFixed(0)} percent of the target`}>
            <circle cx="75" cy="75" r="62" fill="none" stroke="var(--sidebar-accent)" strokeWidth="12" />
            <circle cx="75" cy="75" r="62" fill="none" stroke="var(--gold)" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${(pct / 100) * C} ${C}`} transform="rotate(-90 75 75)" />
            <text x="75" y="80" textAnchor="middle" fontSize="30" fontWeight="600" fontFamily="var(--font-mono)" fill="#fff">
              {pct.toFixed(0)}%
            </text>
            <text x="75" y="100" textAnchor="middle" fontSize="11.5" fill="var(--sidebar-foreground)">
              {daysLeft} {daysLeft === 1 ? "day" : "days"} left
            </text>
          </svg>
          <div className="flex justify-between font-mono text-sm">
            <span>
              <span className="block font-sans text-[11.5px] text-sidebar-foreground">Won</span>
              {fmtMoney(k.revenue)}
            </span>
            <span className="text-right">
              <span className="block font-sans text-[11.5px] text-sidebar-foreground">To go</span>
              {fmtMoney(gap)}
            </span>
          </div>
        </>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-6 text-center">
          <p className="text-sm text-sidebar-foreground">No target is set for this month.</p>
          <Link to="/targets" className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20">
            Set targets
          </Link>
        </div>
      )}
    </section>
  );
}

/* ---------- Funnel ---------- */

/** Leads to won over the last 90 days, each step with the share that made it from the step before */
export function ConversionFunnelTile({ funnel, className }: { funnel: FunnelData; className?: string }) {
  const max = Math.max(1, ...funnel.steps.map((s) => s.count));
  const fills = ["oklch(0.36 0.13 268)", "oklch(0.42 0.16 266)", "var(--primary)", "oklch(0.6 0.15 250)", "var(--gold)"];
  return (
    <Tile title={`Funnel · last ${funnel.days} days`} className={className}>
      {funnel.steps[0]?.count === 0 ? (
        <EmptyLine>No leads or deals in this period yet.</EmptyLine>
      ) : (
        <ol className="flex flex-col gap-2">
          {funnel.steps.map((s, i) => {
            const w = Math.max(8, Math.round((s.count / max) * 100));
            return (
              <li key={s.key} className="flex flex-col items-center gap-0.5">
                <span className="h-[22px] rounded" style={{ width: `${w}%`, background: fills[i] }} />
                <span className="text-xs text-muted-foreground">
                  {s.label} <strong className="font-mono font-semibold text-foreground">{s.count}</strong>
                  {s.rate !== null && <span> {s.rate}%</span>}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </Tile>
  );
}

export function FunnelTile({ stages, className }: { stages: ManagerData["stages"]; className?: string }) {
  const rows = stages.filter((s) => s.deals > 0).slice(0, 7);
  const max = Math.max(1, ...rows.map((s) => s.amount));
  return (
    <Tile title="Pipeline by stage" subtitle="Open deals, by amount" className={className} action={<Link to="/deals" className="text-xs font-medium text-primary hover:underline">Open pipeline</Link>}>
      {rows.length === 0 ? (
        <EmptyLine>No open deals.</EmptyLine>
      ) : (
        <ol className="flex flex-col gap-2">
          {rows.map((s) => (
            <li key={s.id} className="flex flex-col items-center gap-0.5">
              <span className="h-5 rounded-md bg-primary" style={{ width: `${Math.max(12, (s.amount / max) * 100)}%`, opacity: 0.35 + 0.65 * (s.amount / max) }} />
              <span className="text-xs text-muted-foreground">
                {s.name} <strong className="font-mono font-semibold text-foreground">{s.deals}</strong> · <span className="font-mono">{fmtMoney(s.amount)}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </Tile>
  );
}

/* ---------- Leaderboard ---------- */

export function LeaderTile({ title, leaders, className }: { title: string; leaders: Leader[]; className?: string }) {
  return (
    <Tile title={title} subtitle="Revenue won this month" className={className} action={<Link to="/reports" className="text-xs font-medium text-primary hover:underline">Full report</Link>}>
      {leaders.length === 0 ? (
        <EmptyLine>No wins recorded yet this month.</EmptyLine>
      ) : (
        <ol className="flex flex-col">
          {leaders.map((l, i) => {
            const pct = l.target > 0 ? Math.min(100, (l.won / l.target) * 100) : null;
            return (
              <li key={l.id} className="flex items-center gap-3 border-t border-border py-2 first:border-t-0">
                <span className={cn("w-4 font-mono text-sm font-bold", i === 0 ? "text-gold-ink" : "text-muted-foreground")}>{i + 1}</span>
                <span className={cn("flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold", i === 0 ? "bg-gold text-sidebar" : "bg-accent text-accent-foreground")}>
                  {l.name
                    .split(" ")
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join("")}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{l.name}</span>
                  <span className="block text-[11.5px] text-muted-foreground">
                    {l.deals} {l.deals === 1 ? "deal" : "deals"} won{pct !== null ? ` · ${pct.toFixed(0)}% of target` : ""}
                  </span>
                </span>
                <span className="font-mono text-sm font-semibold">{fmtMoney(l.won)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </Tile>
  );
}

/* ---------- Needs attention ---------- */

const ATTN_TONE: Record<AttentionRow["tone"], string> = { danger: "bg-danger-soft text-danger", warning: "bg-warning-soft text-warning", info: "bg-info-soft text-info" };

export function AttentionTile({ rows, className }: { rows: AttentionRow[]; className?: string }) {
  return (
    <Tile title="Needs attention" subtitle="Where the team is stuck" className={className}>
      {rows.length === 0 ? (
        <div className="flex flex-col items-center gap-1 py-6 text-center">
          <span className="flex size-9 items-center justify-center rounded-full bg-success-soft text-success">
            <ShieldCheck className="size-5" />
          </span>
          <p className="text-xs text-muted-foreground">Nothing is stuck right now.</p>
        </div>
      ) : (
        <ul className="flex flex-col">
          {rows.map((r) => (
            <li key={r.key} className="border-t border-border first:border-t-0">
              <Link to={r.to} className="flex items-center gap-2.5 py-2 hover:bg-muted/40">
                <span className={cn("flex size-6 shrink-0 items-center justify-center rounded-md", ATTN_TONE[r.tone])}>
                  <AlertCircle className="size-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{r.label}</span>
                  {r.detail && <span className="block truncate text-[11.5px] text-muted-foreground">{r.detail}</span>}
                </span>
                {r.count !== undefined && <span className="font-mono text-sm font-semibold">{r.count}</span>}
                <ArrowRight className="size-3.5 text-muted-foreground" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Tile>
  );
}

/* ---------- Quotes waiting for approval ---------- */

/** "3h", "2d": how long something has waited */
const waited = (iso: string) => {
  const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  return min < 60 ? `${Math.max(1, min)}m` : min < 1440 ? `${Math.floor(min / 60)}h` : `${Math.floor(min / 1440)}d`;
};

export function ApprovalsTile({ rows, className }: { rows: ManagerData["approvals"]; className?: string }) {
  return (
    <Tile title="Waiting for your approval" subtitle="Quotes and proposals your team has sent up" className={className} action={<Link to="/quotes" className="text-xs font-medium text-primary hover:underline">All quotes</Link>}>
      {rows.length === 0 ? (
        <EmptyLine>Nothing is waiting for your approval.</EmptyLine>
      ) : (
        <ul className="flex flex-col">
          {rows.slice(0, 5).map((q) => (
            <li key={q.id} className="border-t border-border first:border-t-0">
              <Link to={q.kind === "PROPOSAL" ? `/proposals/${q.id}` : `/quotes/${q.id}`} className="flex items-center gap-3 py-2 hover:bg-muted/40">
                <span className="min-w-0 flex-1">
                  <span className="block font-mono text-[13px] font-semibold">
                    {q.display_number}
                    <span className="ml-2 rounded-md bg-muted px-1.5 py-0.5 font-sans text-[10.5px] font-semibold text-muted-foreground">{q.kind === "PROPOSAL" ? "Proposal" : "Quote"}</span>
                  </span>
                  <span className="block truncate text-[11.5px] text-muted-foreground">
                    {q.account_name} · {q.owner_name}
                    {q.kind !== "PROPOSAL" && Number(q.max_discount_pct) > 0 ? ` · up to ${Number(q.max_discount_pct)}% off` : ""}
                    {q.submitted_at ? ` · waiting ${waited(q.submitted_at)}` : ""}
                  </span>
                </span>
                <span className="font-mono text-sm font-semibold">{fmtMoney(q.grand_total)}</span>
                <span className="rounded-md bg-accent px-2 py-1 text-xs font-semibold text-accent-foreground">Review</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Tile>
  );
}

/* ---------- Proposals customers have opened ---------- */

export function HotTile({ rows, className }: { rows: ProposalListRow[]; className?: string }) {
  return (
    <Tile title="Hot right now" subtitle="Customers who opened a proposal" className={className} action={<Link to="/proposals" className="text-xs font-medium text-primary hover:underline">All proposals</Link>}>
      {rows.length === 0 ? (
        <EmptyLine>No proposals have been opened and left waiting.</EmptyLine>
      ) : (
        <ul className="flex flex-col">
          {rows.slice(0, 5).map((p) => (
            <li key={p.id} className="border-t border-border first:border-t-0">
              <Link to={`/proposals/${p.id}`} className="flex items-center gap-3 py-2 hover:bg-muted/40">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gold-soft text-gold-ink">
                  <Eye className="size-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{p.account?.name || p.deal?.name || p.title}</span>
                  <span className="block truncate text-[11.5px] text-muted-foreground">
                    {p.display_number} · {p.owner ? `${p.owner.first_name} ${p.owner.last_name || ""}`.trim() : "unassigned"} · opened {p.view_count}×
                  </span>
                </span>
                <span className="flex gap-0.5" role="img" aria-label={p.view_count >= 3 ? "Very hot" : p.view_count === 2 ? "Hot" : "Warm"}>
                  {[1, 2, 3].map((n) => (
                    <span key={n} className={cn("h-3.5 w-1 rounded-sm", n <= Math.min(3, Math.max(1, p.view_count)) ? "bg-gold" : "bg-border")} />
                  ))}
                </span>
                <span className="w-16 text-right font-mono text-sm font-semibold">{fmtMoney(Number(p.estimate_total))}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Tile>
  );
}

/* ---------- Today's agenda ---------- */

const TASK_COLOR: Record<string, string> = {
  CALL: "bg-accent text-accent-foreground",
  MEETING: "bg-info-soft text-info",
  EMAIL: "bg-info-soft text-info",
  WHATSAPP: "bg-success-soft text-success",
  FOLLOW_UP: "bg-warning-soft text-warning",
  OTHER: "bg-muted text-muted-foreground",
};

export function AgendaTile({ tasks, className }: { tasks: Task[]; className?: string }) {
  const hourOf = (t: Task) => new Date(t.due_at).getHours();
  const nowH = new Date().getHours();
  const first = Math.min(9, ...tasks.map(hourOf));
  const last = Math.max(17, ...tasks.map(hourOf));
  const hours = Array.from({ length: last - first + 1 }, (_, i) => first + i);
  return (
    <Tile title={`Today · ${tasks.length} ${tasks.length === 1 ? "item" : "items"}`} subtitle="Your own calls, meetings and follow-ups" className={className} action={<Link to="/calendar" className="text-xs font-medium text-primary hover:underline">Open calendar</Link>}>
      {tasks.length === 0 ? (
        <EmptyLine>Nothing is scheduled for you today.</EmptyLine>
      ) : (
        <div className="overflow-x-auto">
          <div className="grid min-w-[720px] gap-px" style={{ gridTemplateColumns: `repeat(${hours.length}, minmax(0, 1fr))` }}>
            {hours.map((h) => {
              const here = tasks.filter((t) => hourOf(t) === h);
              return (
                <div key={h} className={cn("flex min-h-[88px] flex-col gap-1 border-l border-border px-1.5", h === nowH && "border-l-2 border-l-danger")}>
                  <span className={cn("font-mono text-[11px]", h === nowH ? "font-bold text-danger" : "text-muted-foreground")}>{String(h).padStart(2, "0")}:00</span>
                  {here.map((t) => (
                    <Link key={t.id} to="/tasks" className={cn("rounded-md px-1.5 py-1 text-[11.5px] leading-tight hover:opacity-80", TASK_COLOR[t.task_type] || TASK_COLOR.OTHER)}>
                      <span className="block truncate font-semibold">{t.title}</span>
                      <span className="block truncate opacity-80">{t.related_name || new Date(t.due_at).toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }))}</span>
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Tile>
  );
}

